import { z } from 'zod';
import { SectionSchemaZod } from './section.schema.zod';
import { ValidatorRefZod } from './validator.ref.zod';
import { JsonValueZod } from './json.value.zod';
import { SectionColumnSchemaZod } from './section.column.schema.zod';

export const FormTabSchemaZod = z.object({
  id: z.string().min(1),
  type: z.literal('form'),
  title: z.string().optional(),
  sections: z.array(SectionSchemaZod),
  sectionColumns: z.array(SectionColumnSchemaZod).min(1).optional(),
  validators: z.array(ValidatorRefZod).optional(),
}).strict();

export const RelationTabSchemaZod = z.object({
  id: z.string().min(1),
  type: z.literal('relation'),
  title: z.string().optional(),
  /**
   * Which related entity this tab lists — an `ActivityEntityName` value.
   *
   * Typed as a string rather than a native enum because this schema is PERSISTED: a stored value must
   * survive an enum member being renamed or removed without failing to parse, and Zod would reject an
   * unknown one outright. Consumers narrow it to `ActivityEntityName` where the manifest is resolved, and
   * an unresolvable value renders no tab rather than throwing.
   */
  relation: z.string().min(1),
}).strict();

/** One switch segment of a relation group: the same persisted relation key a relation tab carries. */
const RelationGroupSegmentSchemaZod = z.object({
  id: z.string().regex(/^[a-z][a-zA-Z0-9]*$/),
  title: z.string().optional(),
  relation: z.string().min(1),
}).strict();

/**
 * Several relation boards behind one tab and a segment switch. `id` doubles as the URL value, so it is a
 * plain identifier. `param` names the query param that keeps the segment; it must not collide with
 * `tab` (the host's own) or a board's `view` / `segment` / `f.*`, which is why it is authored, not derived.
 */
export const RelationGroupTabSchemaZod = z.object({
  id: z.string().min(1),
  type: z.literal('relationGroup'),
  title: z.string().optional(),
  param: z.string().regex(/^[a-z][a-zA-Z0-9]*$/).refine(param => !['tab', 'view', 'segment', 'show'].includes(param), 'Reserved query param').optional(),
  relations: z.array(RelationGroupSegmentSchemaZod).min(2),
}).strict();

export const CustomTabSchemaZod = z.object({
  id: z.string().min(1),
  type: z.literal('custom'),
  title: z.string().optional(),
  renderer: z.string().min(1),
  config: z.record(z.string(), JsonValueZod).optional(),
}).strict();

/**
 * Tab whose entire content is a child Form surface supplied by a runtime
 * `formDefinitions` capability. Child fields stay out of the outer model.
 */
export const FormDefinitionTabSchemaZod = z.object({
  id: z.string().min(1),
  type: z.literal('formDefinition'),
  title: z.string().optional(),
  /** Runtime capability key under `formDefinitions`. */
  definition: z.string().min(1),
}).strict();

export const TabSchemaZod = z.discriminatedUnion('type', [FormTabSchemaZod, RelationTabSchemaZod, RelationGroupTabSchemaZod, CustomTabSchemaZod, FormDefinitionTabSchemaZod]);

export type FormTabSchema = z.infer<typeof FormTabSchemaZod>;
export type RelationTabSchema = z.infer<typeof RelationTabSchemaZod>;
export type RelationGroupTabSchema = z.infer<typeof RelationGroupTabSchemaZod>;
export type CustomTabSchema = z.infer<typeof CustomTabSchemaZod>;
export type FormDefinitionTabSchema = z.infer<typeof FormDefinitionTabSchemaZod>;
export type TabSchema = z.infer<typeof TabSchemaZod>;

export const EntityBoardRelationTabSchemaZod = RelationTabSchemaZod;
export const EntityDashboardRelationTabSchemaZod = RelationTabSchemaZod;
export type EntityBoardRelationTabSchema = RelationTabSchema;
export type EntityDashboardRelationTabSchema = RelationTabSchema;
